import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { ChartTooltip, gridProps, axisX, axisY } from '../lib/chartTheme';
import {
  MdDashboard, MdAddBox, MdAssignment, MdLocationOn,
  MdInventory, MdWarning, MdCheckCircle, MdTrendingUp,
  MdHistory, MdPeople, MdDescription, MdPlayArrow, MdChevronRight,
} from 'react-icons/md';
import api from '../api/axios';
import { useChangeStream } from '../lib/useChangeStream';
import PlatformStats from '../components/PlatformStats';

const STATUS_COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#f44336', '#9C27B0', '#00BCD4'];

// Hue palette for the bar chart — one colour per parent location. Children
// of a parent share the parent's hue with a slight lightness shift so the
// hierarchy is visible at a glance.
const HUE_PALETTE = [
  { dark: '#1a3c6e', light: '#5478a8' },  // navy
  { dark: '#2e7d32', light: '#66bb6a' },  // green
  { dark: '#e65100', light: '#ff9800' },  // orange
  { dark: '#6a1b9a', light: '#ab47bc' },  // purple
  { dark: '#00838f', light: '#26c6da' },  // cyan
  { dark: '#c62828', light: '#ef5350' },  // red
];

// Compact stat used inside the Audit Operations card.
const MiniStat = ({ label, value, sub, color }) => (
  <div style={{ background: '#f8fafc', border: '1px solid var(--border-light)', borderRadius: 10, padding: '10px 12px' }}>
    <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, marginTop: 2 }}>{label}</div>
    {sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [statsData, setStatsData] = useState(null);
  const [auditList, setAuditList] = useState([]);
  const [recentAudits, setRecentAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [stats, audits] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/audit'),
      ]);
      setStatsData(stats.data);
      setAuditList(audits.data);
      setRecentAudits(audits.data.slice(0, 5));
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);
  // Auto-refresh on any data change so the tiles stay live.
  useChangeStream(() => { fetchAll(); }, ['assets', 'locations', 'audits', 'audit_items', 'asset_movements']);

  // Pre-compute the bar-chart data + per-bar colour map. Parent locations get
  // the dark hue from their slot in HUE_PALETTE; their children get the light
  // hue from the same slot, so the hierarchy reads as colour grouping.
  const byLocationData = (() => {
    const all = statsData?.byLocation || [];
    // Group by root parent — anything with parent_id=null is a root, anything
    // else takes the colour assigned to its (root) ancestor.
    const rootIndex = new Map();
    let nextHue = 0;
    for (const l of all) {
      if (l.parent_id == null && !rootIndex.has(l.id)) {
        rootIndex.set(l.id, nextHue++ % HUE_PALETTE.length);
      }
    }
    return all.map(l => {
      const isRoot = l.parent_id == null;
      const rootId = isRoot ? l.id : l.parent_id;
      const slot = rootIndex.get(rootId) ?? 0;
      const palette = HUE_PALETTE[slot % HUE_PALETTE.length];
      return {
        name: l.name?.substring(0, 18) || l.code || `#${l.id}`,
        count: l.count,
        fill: isRoot ? palette.dark : palette.light,
      };
    });
  })();

  const statusDist = (statsData?.statusBreakdown || []).map(s => ({
    name: s.status?.charAt(0).toUpperCase() + s.status?.slice(1),
    value: s.count,
  }));

  const counts = statsData?.counts || { totalAssets: 0, activeAssets: 0, totalLocations: 0, openAudits: 0 };
  const lastUpdated = statsData?.lastUpdated;
  const onlineUsers = statsData?.onlineUsers || { count: 0, users: [] };
  const lastAuditDateStr = statsData?.lastAudit?.audit_date
    ? new Date(statsData.lastAudit.audit_date).toLocaleDateString()
    : 'None';

  // ── Operational widgets (replace the low-value time-series charts) ──────────
  const relTime = (d) => {
    if (!d) return '—';
    const diff = Date.now() - new Date(d).getTime();
    const day = 86400000;
    if (diff < 0) return 'just now';
    if (diff < day) return 'today';
    if (diff < 2 * day) return 'yesterday';
    return `${Math.floor(diff / day)}d ago`;
  };
  // auditList is newest-first from /audit.
  const activeAudits = auditList.filter(a => a.status === 'in_progress' || a.status === 'paused');
  const lastAudited = recentAudits[0] || null;
  const lastAuditAccuracy = lastAudited && lastAudited.total_assets > 0
    ? Math.round((lastAudited.scanned_assets / lastAudited.total_assets) * 100) : null;
  const openMissing = activeAudits.reduce((s, a) => s + (a.missing_assets || 0), 0);
  // Completed audits that still have missing items → need follow-up.
  const missingAudits = recentAudits.filter(a => a.status === 'completed' && (a.missing_assets || 0) > 0);
  // Locations that have assets but have never appeared in any audit.
  const auditedLocs = new Set(auditList.map(a => a.location_name).filter(Boolean));
  const neverAudited = (statsData?.byLocation || [])
    .filter(l => (l.count || 0) > 0 && l.name && !auditedLocs.has(l.name))
    .map(l => l.name);

  // Build the actionable list (capped).
  const actionItems = [];
  activeAudits.forEach(a => actionItems.push({
    color: '#2563EB',
    title: `Resume audit: ${a.audit_name || `Audit #${a.id}`}`,
    sub: `${a.location_name || '—'} · ${a.scanned_assets}/${a.total_assets} scanned`,
    action: 'Resume', to: '/asset-audit',
  }));
  missingAudits.forEach(a => actionItems.push({
    color: '#EF4444',
    title: `${a.missing_assets} missing in ${a.location_name || a.audit_name}`,
    sub: `from "${a.audit_name || `Audit #${a.id}`}" · review & follow up`,
    action: 'Report', to: `/audit-report?audit_id=${a.id}`,
  }));
  if (neverAudited.length) actionItems.push({
    color: '#ef6c00',
    title: `${neverAudited.length} location(s) never audited`,
    sub: neverAudited.slice(0, 3).join(', ') + (neverAudited.length > 3 ? '…' : ''),
    action: 'Audit', to: '/asset-audit',
  });
  const actionItemsCapped = actionItems.slice(0, 6);

  const statCards = [
    { label: 'Total Assets', value: counts.totalAssets, icon: <MdInventory />, color: '#1a3c6e', bg: '#e8edf5' },
    { label: 'Active Assets', value: counts.activeAssets, icon: <MdCheckCircle />, color: '#2e7d32', bg: '#e8f5e9' },
    { label: 'Locations', value: counts.totalLocations, icon: <MdLocationOn />, color: '#1565c0', bg: '#e3f2fd' },
    { label: 'Last Audit', value: lastAuditDateStr, icon: <MdTrendingUp />, color: '#e65100', bg: '#fff3e0', small: true },
    {
      label: 'Last Updated Asset',
      value: lastUpdated ? lastUpdated.asset_name : '—',
      sub: lastUpdated ? `${lastUpdated.location_name || 'No location'} · ${lastUpdated.status}` : null,
      icon: <MdHistory />, color: '#6a1b9a', bg: '#f3e5f5', small: true,
    },
    {
      label: 'Users Online',
      value: onlineUsers.count,
      sub: onlineUsers.count > 0 ? onlineUsers.users.map(u => u.username).join(', ').slice(0, 60) : null,
      icon: <MdPeople />, color: '#00695c', bg: '#e0f2f1',
    },
  ];

  if (loading) return <div className="loading-spinner"><div className="spinner" /><span>Loading dashboard...</span></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title"><MdDashboard /> Dashboard</div>
          <div className="page-subtitle">Welcome to the Asset Management System</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={() => navigate('/asset-registration')}>
            <MdAddBox /> Register Asset
          </button>
          <button className="btn btn-accent" onClick={() => navigate('/asset-audit')}>
            <MdAssignment /> Start Audit
          </button>
        </div>
      </div>

      {/* Platform-owner section. Renders ONLY for the super-admin (admin
          role inside the tenant flagged is_platform_owner). For every
          other tenant this returns null and the regular dashboard starts
          right after this. So Bata's admin sees their normal dashboard
          unchanged; Identium's admin sees this PLUS the normal dashboard
          underneath. */}
      <PlatformStats />

      {/* Stats — now 6 tiles including Last Updated Asset + Users Online */}
      <div className="stats-grid">
        {statCards.map((card, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ background: card.bg, color: card.color }}>
              {card.icon}
            </div>
            <div className="stat-info">
              <div className="stat-value" style={{ color: card.color, fontSize: card.small ? '1.2rem' : '1.8rem' }} title={typeof card.value === 'string' ? card.value : undefined}>
                {card.value}
              </div>
              <div className="stat-label">{card.label}</div>
              {card.sub && (
                <div style={{ fontSize: 11, color: '#78909c', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={card.sub}>
                  {card.sub}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Charts row 1 — bar (assets per location) + pie (status) */}
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Bar chart — parent locations + child locations share a colour
            family so the hierarchy reads at a glance. */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><MdLocationOn style={{ color: 'var(--accent)' }} /> Assets per Location</div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byLocationData} margin={{ top: 5, right: 10, left: -10, bottom: 30 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="name" {...axisX} angle={-30} textAnchor="end" interval={0} />
                <YAxis {...axisY} allowDecimals={false} />
                <Tooltip cursor={{ fill: 'rgba(37,99,235,0.06)' }} content={<ChartTooltip />} />
                <Bar dataKey="count" radius={[6,6,0,0]} name="Assets" maxBarSize={48}>
                  {byLocationData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie chart */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><MdInventory style={{ color: 'var(--accent)' }} /> Asset Status Distribution</div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusDist} dataKey="value" nameKey="name"
                  cx="50%" cy="50%" innerRadius={52} outerRadius={92} paddingAngle={2}
                  stroke="#fff" strokeWidth={2}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {statusDist.map((_, index) => (
                    <Cell key={index} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Audit operations — actionable snapshot instead of low-value charts */}
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

        {/* Active audit + quick audit stats */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><MdAssignment style={{ color: 'var(--accent)' }} /> Audit Operations</div>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/audit-report')}>
              <MdDescription /> Audit Reports
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
            <MiniStat label="Active audits" value={activeAudits.length} color="#2563EB" />
            <MiniStat label="Open missing" value={openMissing} color="#EF4444" />
            <MiniStat
              label="Last audited"
              value={lastAuditAccuracy != null ? `${lastAuditAccuracy}%` : '—'}
              sub={lastAudited ? `${lastAudited.location_name || '—'} · ${relTime(lastAudited.audit_date)}` : 'no audits yet'}
              color="#10B981"
            />
          </div>

          {activeAudits.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '20px', background: '#f8fafc', borderRadius: 10, color: 'var(--text-secondary)', fontSize: 13 }}>
              No active audit session.
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/asset-audit')}><MdPlayArrow /> Start audit</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {activeAudits.slice(0, 3).map(a => {
                const pct = a.total_assets > 0 ? Math.round((a.scanned_assets / a.total_assets) * 100) : 0;
                return (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--border-light)', borderRadius: 10, background: '#fcfdff' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.audit_name || `Audit #${a.id}`}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>📍 {a.location_name || '—'} · {a.scanned_assets}/{a.total_assets} scanned · {pct}%</div>
                      <div className="progress-bar-wrap" style={{ marginTop: 6 }}>
                        <div className="progress-bar-fill" style={{ width: `${pct}%`, background: pct === 100 ? 'var(--success)' : 'var(--accent)' }} />
                      </div>
                    </div>
                    <span className={`badge ${a.status === 'paused' ? 'badge-warning' : 'badge-info'}`} style={{ flexShrink: 0 }}>
                      {a.status === 'paused' ? 'PAUSED' : 'LIVE'}
                    </span>
                    <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={() => navigate('/asset-audit')}>Continue</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action items — things that need attention */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><MdWarning style={{ color: '#ef6c00' }} /> Action Items</div>
            {actionItems.length > 0 && <span className="badge badge-warning">{actionItems.length}</span>}
          </div>
          {actionItemsCapped.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '28px', color: 'var(--success)', fontSize: 14, fontWeight: 500 }}>
              <MdCheckCircle /> All clear — nothing needs attention.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {actionItemsCapped.map((it, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: i < actionItemsCapped.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: it.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</div>
                    {it.sub && <div style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.sub}</div>}
                  </div>
                  <button className="btn btn-outline btn-sm" style={{ flexShrink: 0 }} onClick={() => navigate(it.to)}>
                    {it.action} <MdChevronRight />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>


      {/* Recent Audits */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><MdAssignment style={{ color: 'var(--accent)' }} /> Recent Audits</div>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/audit-report')}>View All</button>
        </div>
        {recentAudits.length === 0 ? (
          <div className="empty-state" style={{ padding: '24px' }}>
            <p>No audits yet. <button className="btn btn-accent btn-sm" onClick={() => navigate('/asset-audit')}>Start your first audit</button></p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Audit Name</th>
                  <th>Location</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Scanned</th>
                  <th>Missing</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentAudits.map(audit => (
                  <tr key={audit.id}>
                    <td style={{ fontWeight: 500 }}>{audit.audit_name}</td>
                    <td>{audit.location_name}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                      {new Date(audit.audit_date).toLocaleString()}
                    </td>
                    <td>{audit.total_assets}</td>
                    <td><span className="badge badge-success">{audit.scanned_assets}</span></td>
                    <td>
                      {audit.missing_assets > 0
                        ? <span className="badge badge-danger">{audit.missing_assets}</span>
                        : <span className="badge badge-success">0</span>
                      }
                    </td>
                    <td>
                      <span className={`badge ${audit.status === 'completed' ? 'badge-success' : 'badge-warning'}`}>
                        {audit.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <div className="card-title">Quick Actions</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: 'Register Asset', path: '/asset-registration', color: 'btn-primary', icon: <MdAddBox /> },
            { label: 'Start Audit', path: '/asset-audit', color: 'btn-accent', icon: <MdAssignment /> },
            { label: 'Search Asset', path: '/asset-search', color: 'btn-success', icon: <MdInventory /> },
            { label: 'View Reports', path: '/reports', color: 'btn-warning', icon: <MdTrendingUp /> },
            { label: 'Handheld Sim', path: '/handheld-simulator', color: 'btn-outline', icon: <MdWarning /> },
          ].map(a => (
            <button key={a.path} className={`btn ${a.color}`} onClick={() => navigate(a.path)}>
              {a.icon} {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
